import React from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui/Container';
import { TrendingDown, AlertCircle, Heart } from 'lucide-react';
import healthFuture from '../../assets/pitch-deck/preventative-health-future.png';

const ProblemOpportunitySection: React.FC = () => {
  return (
    <section className="py-32 bg-theme-bg relative overflow-hidden">
      {/* Background Decorative Blur */}
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-theme-error/5 blur-[120px] rounded-full -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-theme-primary/5 blur-[120px] rounded-full translate-x-1/2 translate-y-1/2" />

      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          {/* Left: The Problem */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-theme-error/10 border border-theme-error/20 text-theme-error text-xs font-bold uppercase tracking-widest mb-6">
              <AlertCircle className="w-3 h-3" />
              <span>The Problem</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-8 leading-tight text-theme-text-primary">
              Reactive Medicine is <br /> 
              <span className="text-theme-error">Broken.</span>
            </h2>
            <p className="text-xl text-theme-text-secondary mb-10 leading-relaxed">
              For decades, "health" was defined as the absence of disease. Annual physicals and generic screenings are snapshot moments that catch problems after they've already begun.
            </p>
            
            <div className="space-y-6">
              <div className="flex space-x-4 p-4 rounded-2xl bg-theme-surface border border-theme-border shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-theme-error/10 flex items-center justify-center flex-shrink-0">
                  <TrendingDown className="w-6 h-6 text-theme-error" />
                </div>
                <div>
                  <h4 className="font-bold text-theme-text-primary">Static Data</h4>
                  <p className="text-sm text-theme-text-secondary">Annual checkups provide no trend analysis, missing 90% of physiological shifts.</p>
                </div>
              </div>
              <div className="flex space-x-4 p-4 rounded-2xl bg-theme-surface border border-theme-border shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-theme-error/10 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-theme-error" />
                </div>
                <div>
                  <h4 className="font-bold text-theme-text-primary">Generic Standards</h4>
                  <p className="text-sm text-theme-text-secondary">"Normal" ranges are based on populations, not your unique biological optimal.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: The Opportunity */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="aspect-square rounded-[48px] overflow-hidden shadow-2xl relative group">
              <img 
                src={healthFuture} 
                alt="Future of preventative health"
                className="w-full h-full object-cover grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-12 left-12 right-12">
                <h3 className="text-3xl font-black text-white mb-4 italic">The Longevity Revolution</h3>
                <p className="text-white/80 leading-relaxed font-light">
                  "The shift from <strong>'Reactive'</strong> to <strong>'Proactive'</strong> is the largest capital movement in healthcare history."
                </p>
                <div className="mt-6 flex items-center space-x-4">
                  <div className="px-4 py-2 rounded-lg bg-theme-primary text-theme-text-inverse font-black text-lg">
                    $25T+
                  </div>
                  <span className="text-sm text-white/60 tracking-widest uppercase font-bold">Total Addressable Market</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default ProblemOpportunitySection;
